import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Users, Wrench } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import { canAccessPage } from '../../config/gatedPages';
import { useLocation } from '../../contexts/LocationContext';
import { ActionButton } from '../common/ActionButton';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';
import { getErrorMessage } from '../../utils/network.utils';

// /api/admin/clocked-in returns one row per OPEN TIME ENTRY. Count distinct
// people, not rows — duplicate open entries (e.g. from a double-fired admin
// create) made this tile read 31 while every other view said 28 (2026-08-05).
// Matches the bfav5 dashboard, which uses COUNT(DISTINCT employee_id).
const countClockedInPeople = (rows: Array<{ employee_id: number }> | null | undefined): number =>
  rows ? new Set(rows.map(r => r.employee_id)).size : 0;

export const AdminDashboard: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const { ensureFreshLocation } = useLocation();
  const [initializing, setInitializing] = useState(true);
  const [initFailed, setInitFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [navigating, setNavigating] = useState(false);

  // KPI state
  const [clockedInCount, setClockedInCount] = useState<number>(0);
  const [equipmentIssuesCount, setEquipmentIssuesCount] = useState<number>(0);

  // Check if user is admin — admins don't need location, skip the wait
  const isAdmin = auth?.user?.role === 'admin';

  /**
   * Navigate to a module after ensuring fresh location (uses 5-min cache)
   * Admins skip the location check entirely — location is optional for them
   */
  const navigateWithLocationRefresh = async (path: string, options?: { state?: any }) => {
    if (isAdmin) {
      // Admins don't need location — navigate immediately
      navigate(path, options);
      return;
    }
    setNavigating(true);
    try {
      await ensureFreshLocation();
      navigate(path, options);
    } finally {
      setNavigating(false);
    }
  };

  useEffect(() => {
    const initializeDashboard = async () => {
      try {
        setInitFailed(false);
        setInitializing(true);

        // No location request here on purpose: some admins don't want their
        // location read at all. Only the Time Clock and Machine Hub pages ask for
        // a fix, and only when they are opened.

        // Fetch KPI data in parallel
        const [clockedInResponse, equipmentIssuesResponse] = await Promise.all([
          api.get(API_CONFIG.ENDPOINTS.CLOCKED_IN_EMPLOYEES),
          api.get(`${API_CONFIG.ENDPOINTS.EQUIPMENT_ISSUES_LIST}?status=open`).catch(() => ({ data: { success: false, data: [] } }))
        ]);

        // Set clocked in count
        if (clockedInResponse.data.success) {
          setClockedInCount(countClockedInPeople(clockedInResponse.data.data));
        }

        // Set equipment issues count
        if (equipmentIssuesResponse.data.success) {
          setEquipmentIssuesCount(equipmentIssuesResponse.data.data?.length || 0);
        }

        setInitializing(false);
        setInitFailed(false);
      } catch (error) {
        console.error('Error initializing dashboard:', error);
        setErrorMessage(getErrorMessage(error, 'en'));
        setInitFailed(true);
        setInitializing(false);
      }
    };

    // Retry initialization when coming back online
    const handleOnline = () => {
      console.log('Network restored - retrying dashboard initialization');
      if (auth?.user?.employee_id) {
        initializeDashboard();
      }
    };

    window.addEventListener('online', handleOnline);

    if (auth?.user?.employee_id) {
      initializeDashboard();
      // Log dashboard visit (fire and forget), tagged as a PWA visit so the
      // super-admin activity view can split PWA vs bfav5.
      api.post('/api/admin/dashboard/visit', { page: 'dashboard', source: 'pwa' }).catch(() => {});
    }

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [auth?.user?.employee_id]);

  // Refresh the KPI counts when the app returns to the foreground. The PWA is a
  // single-page app that stays mounted across backgrounding, so the mount-time
  // fetch above goes stale on reopen (clock-in count was showing old numbers).
  // This re-fetches quietly (no loading screen) on focus / tab-visible.
  useEffect(() => {
    if (!auth?.user?.employee_id) return;
    const refresh = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const [ci, ei] = await Promise.all([
          api.get(API_CONFIG.ENDPOINTS.CLOCKED_IN_EMPLOYEES),
          api.get(`${API_CONFIG.ENDPOINTS.EQUIPMENT_ISSUES_LIST}?status=open`).catch(() => ({ data: { success: false, data: [] } })),
        ]);
        if (ci.data.success) setClockedInCount(countClockedInPeople(ci.data.data));
        if (ei.data.success) setEquipmentIssuesCount(ei.data.data?.length || 0);
      } catch { /* keep last good values on a failed refresh */ }
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [auth?.user?.employee_id]);

  // Show loading screen only while initializing
  if (initializing) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}>
        <Loader2
          size={48}
          style={{
            color: 'white',
            animation: 'spin 1s linear infinite'
          }}
        />
        <div style={{
          color: 'white',
          fontSize: 18,
          marginTop: 20,
          fontWeight: '500'
        }}>
          Loading...
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Show loading overlay while navigating
  if (navigating) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}>
        <Loader2
          size={48}
          style={{
            color: 'white',
            animation: 'spin 1s linear infinite'
          }}
        />
        <div style={{
          color: 'white',
          fontSize: 18,
          marginTop: 20,
          fontWeight: '500'
        }}>
          Getting location...
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Error Message - shown when initialization fails */}
      {initFailed && errorMessage && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '12px 16px',
          margin: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>Warning</span>
          <span style={{ color: '#dc2626', fontSize: '14px' }}>{errorMessage}</span>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginLeft: 'auto',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            Retry
          </button>
        </div>
      )}

      <div className="welcome-section">
        <h2 className="welcome-text">Welcome, {auth.user?.first_name}</h2>
        <div className="role-badge admin">ADMINISTRATOR</div>
      </div>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px',
        padding: '0 16px',
        marginTop: '16px'
      }}>
        {/* Clocked In Card */}
        <div
          onClick={() => navigate('/clocked-in')}
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '16px',
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            borderLeft: '4px solid #5b8a8a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div>
            <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
              Clocked In
            </div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b' }}>
              {clockedInCount}
            </div>
          </div>
          <Users size={24} color="#5b8a8a" />
        </div>

        {/* Equipment Issues Card */}
        <div
          onClick={() => navigate('/mechanics/issues')}
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '16px',
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            borderLeft: `4px solid ${equipmentIssuesCount > 0 ? '#b08968' : '#94a3b8'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div>
            <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
              Equip Issues
            </div>
            <div style={{
              fontSize: '28px',
              fontWeight: '700',
              color: equipmentIssuesCount > 0 ? '#b08968' : '#64748b'
            }}>
              {equipmentIssuesCount}
            </div>
          </div>
          <Wrench size={24} color={equipmentIssuesCount > 0 ? '#b08968' : '#94a3b8'} />
        </div>
      </div>

      {/* Job Activity, Daily Summary & Lowboy Location */}
      <div style={{ padding: '0 16px', marginTop: '16px' }}>
        <div className="actions-grid" style={{ padding: 0 }}>
          <ActionButton
            label="Job Activity"
            icon="📊"
            onClick={() => navigateWithLocationRefresh('/job-activity')}
            gradient="linear-gradient(135deg, #64748b 0%, #475569 100%)"
            disabled={navigating}
          />
          <ActionButton
            label="Daily Summary"
            icon="📧"
            onClick={() => navigate('/daily-summary')}
            gradient="linear-gradient(135deg, #64748b 0%, #475569 100%)"
          />
          <ActionButton
            label="Equipment"
            icon="🚜"
            onClick={() => navigate('/fleet')}
            gradient="linear-gradient(135deg, #64748b 0%, #475569 100%)"
          />
          {canAccessPage(auth?.user, 'trucks') && (
            <ActionButton
              label="Trucks"
              icon="🚚"
              onClick={() => navigate('/truck-fleet')}
              gradient="linear-gradient(135deg, #64748b 0%, #475569 100%)"
            />
          )}
          <ActionButton
            label="Lowboy Location"
            icon="🚛"
            onClick={() => navigate('/trucker-tracking')}
            gradient="linear-gradient(135deg, #64748b 0%, #475569 100%)"
          />
          <ActionButton
            label="Truck Schedule"
            icon="🗓️"
            onClick={() => navigate('/truck-schedule')}
            gradient="linear-gradient(135deg, #64748b 0%, #475569 100%)"
          />
          <ActionButton
            label="Bidding"
            icon="📑"
            onClick={() => navigate('/bidding')}
            gradient="linear-gradient(135deg, #64748b 0%, #475569 100%)"
          />
        </div>
      </div>

      {/* Time Management Section */}
      <div style={{ padding: '0 16px', marginTop: '24px' }}>
        <h3 style={{
          fontSize: '14px',
          fontWeight: '600',
          color: '#64748b',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: '12px',
          paddingLeft: '4px'
        }}>
          Time Management
        </h3>
        <div className="actions-grid" style={{ padding: 0 }}>
          <ActionButton
            label="Current Week Time"
            icon="⏰"
            onClick={() => navigate('/time-management')}
            gradient="linear-gradient(135deg, #5b8a8a 0%, #4a7373 100%)"
          />
          {isAdmin && (
            <ActionButton
              label="Approve Time Cards"
              icon="✅"
              onClick={() => navigate('/approve-timecards')}
              gradient="linear-gradient(135deg, #5b8a8a 0%, #4a7373 100%)"
            />
          )}
        </div>
      </div>

      {/* Equipment Management Section */}
      <div style={{ padding: '0 16px', marginTop: '24px' }}>
        <h3 style={{
          fontSize: '14px',
          fontWeight: '600',
          color: '#64748b',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: '12px',
          paddingLeft: '4px'
        }}>
          Equipment Management
        </h3>
        <div className="actions-grid" style={{ padding: 0 }}>
          <ActionButton
            label="Machine Hub"
            icon="⚙️"
            onClick={() => navigate('/mechanics/machine-hub')}
            gradient="linear-gradient(135deg, #a39080 0%, #8b7355 100%)"
          />
          <ActionButton
            label="Inventory"
            icon="📦"
            onClick={() => navigate('/inventory')}
            gradient="linear-gradient(135deg, #a39080 0%, #8b7355 100%)"
          />
          {canAccessPage(auth?.user, 'shop-todo') && (
            <ActionButton
              label="Shop To-Do"
              icon="📋"
              onClick={() => navigate('/shop-todo')}
              gradient="linear-gradient(135deg, #a39080 0%, #8b7355 100%)"
            />
          )}
        </div>
      </div>

      {/* Administration Section */}
      <div style={{ padding: '0 16px', marginTop: '24px' }}>
        <h3 style={{
          fontSize: '14px',
          fontWeight: '600',
          color: '#64748b',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: '12px',
          paddingLeft: '4px'
        }}>
          Administration
        </h3>
        <div className="actions-grid" style={{ padding: 0 }}>
          <ActionButton
            label="Employee Management"
            icon="👥"
            onClick={() => navigate('/employee-management')}
            gradient="linear-gradient(135deg, #7c8594 0%, #5c6370 100%)"
          />
          <ActionButton
            label="Job Management"
            icon="💼"
            onClick={() => navigate('/job-management')}
            gradient="linear-gradient(135deg, #7c8594 0%, #5c6370 100%)"
          />
          <ActionButton
            label="Scan Receipts"
            icon="🧾"
            onClick={() => navigate('/receipts')}
            gradient="linear-gradient(135deg, #7c8594 0%, #5c6370 100%)"
          />
        </div>
      </div>

      {/* Super Admin — only visible to is_super_admin users */}
      {auth?.user?.is_super_admin && (
        <div style={{ padding: '0 16px', marginTop: '24px' }}>
          <h3 style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '12px',
            paddingLeft: '4px'
          }}>
            Super Admin
          </h3>
          <div className="actions-grid" style={{ padding: 0 }}>
            <ActionButton
              label="Trucker Day (test)"
              icon="🛻"
              onClick={() => navigate('/trucker-day')}
              gradient="linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)"
            />
            <ActionButton
              label="My Time"
              icon="🕒"
              onClick={() => navigate('/my-time')}
              gradient="linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)"
            />
            <ActionButton
              label="Link GPS Tracker"
              icon="📡"
              onClick={() => navigate('/link-tracker')}
              gradient="linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)"
            />
            <ActionButton
              label="Manage Trackers"
              icon="🛠️"
              onClick={() => navigate('/manage-trackers')}
              gradient="linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)"
            />
          </div>
        </div>
      )}
    </div>
  );
};
